/**
 * StrategieExpertePage - KI-gestützter Strategie-Experte
 * 
 * Generiert detaillierte Marketing-Strategien für einen gewählten Zeitraum.
 * Nutzt die /api/strategy/generate Endpoint für KI-gestützte Planung.
 */

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Sparkles, Loader2, Calendar, Target, TrendingUp, CheckCircle,
  Clock, ChevronRight, Download, RefreshCw
} from "lucide-react";
import PlatformIcon from '../components/PlatformIcon';
import BrandContextHint from '../components/BrandContextHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StrategieExpertePage = () => {
  const location = useLocation();

  // Form state
  const [zeitraum, setZeitraum] = useState('1 Woche');
  const [startdatum, setStartdatum] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [hauptziel, setHauptziel] = useState('Leads für AVGS-Coaching');
  const [hauptthema, setHauptthema] = useState('');
  const [zielgruppe, setZielgruppe] = useState('Jobcenter-Kunden');
  const [zielgruppeCustom, setZielgruppeCustom] = useState('');
  const [plattformen, setPlattformen] = useState(['instagram', 'facebook']);
  const [tonalitaet, setTonalitaet] = useState('motiviert');
  const [postFrequenz, setPostFrequenz] = useState('3 Posts/Woche');
  const [kampagne, setKampagne] = useState('');

  // Result state
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [strategyId, setStrategyId] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Status
  const [status, setStatus] = useState(null);

  // Prefill from StrategieHistoryPage "Erneut verwenden"
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    if (prefill.zeitraum) setZeitraum(prefill.zeitraum);
    if (prefill.hauptziel) setHauptziel(prefill.hauptziel);
    if (prefill.hauptthema) setHauptthema(prefill.hauptthema);
    if (prefill.zielgruppe) setZielgruppe(prefill.zielgruppe);
    if (prefill.plattformen?.length) setPlattformen(prefill.plattformen);
    if (prefill.tonalitaet) setTonalitaet(prefill.tonalitaet);
    if (prefill.postFrequenz) setPostFrequenz(prefill.postFrequenz);
    if (prefill.kampagne) setKampagne(prefill.kampagne);
  }, [location.state]);

  // Load status and history on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [statusRes, historyRes] = await Promise.all([
          axios.get(`${API}/strategy/status`),
          axios.get(`${API}/strategy/history?limit=10`)
        ]);
        setStatus(statusRes.data);
        setHistory(historyRes.data.strategies || []);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
    };
    loadData();
  }, []);
  
  // Generate strategy
  const handleGenerate = async () => {
    if (!hauptthema.trim()) {
      toast.error('Bitte gib ein Hauptthema ein');
      return;
    }
    if (plattformen.length === 0) {
      toast.error('Wähle mindestens eine Plattform');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/strategy/generate`, {
        zeitraum,
        startdatum,
        hauptziel,
        hauptthema: hauptthema.trim(),
        zielgruppe: zielgruppe === 'custom' ? zielgruppeCustom : zielgruppe,
        plattformen,
        tonalitaet,
        post_frequenz: postFrequenz,
        kampagne: kampagne.trim() || null
      });
      
      if (response.data.success) {
        setStrategy(response.data.strategy);
        setStrategyId(response.data.strategy_id);
        toast.success('Strategie erfolgreich erstellt!');
        
        // Refresh history
        const historyRes = await axios.get(`${API}/strategy/history?limit=10`);
        setHistory(historyRes.data.strategies || []);
      } else {
        toast.error('Strategie-Generierung fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  // Import to batch planner - REAL IMPLEMENTATION
  const handleImportToBatchPlanner = async () => {
    if (!strategyId) {
      toast.error('Keine Strategie-ID vorhanden');
      return;
    }
    
    if (!strategy?.posts || strategy.posts.length === 0) {
      toast.error('Keine Posts zum Importieren vorhanden');
      return;
    }
    
    setImportLoading(true);
    try {
      // Call the actual import endpoint
      const response = await axios.post(`${API}/strategy/import-to-monthly-plan`, {
        strategy_id: strategyId
      });
      
      if (response.data.success) {
        toast.success(`${response.data.imported_count} Posts in Monatsplan importiert!`);
        
        // Navigate to monthly plan after short delay
        setTimeout(() => {
          window.location.href = '/monthly-plan';
        }, 1000);
      } else {
        toast.error('Import fehlgeschlagen');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Import fehlgeschlagen: ' + errorMsg);
    } finally {
      setImportLoading(false);
    }
  };
  
  // Load strategy from history
  const loadFromHistory = (historyItem) => {
    setStrategy(historyItem);
    setStrategyId(historyItem.id);
    setShowHistory(false);
    toast.success('Strategie geladen');
  };
  
  const togglePlatform = (platform) => {
    setPlattformen(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900">
            Strategie-Experte
          </h1>
          <p className="text-stone-500 mt-1">
            KI-gestützte Marketing-Strategie für deinen Zeitraum
          </p>
        </div>
        
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="px-4 py-2 border border-stone-200 rounded-lg hover:bg-stone-50 flex items-center gap-2"
        >
          <Clock size={18} />
          Historie
        </button>
      </div>
      
      {/* History Panel */}
      {showHistory && (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h3 className="font-bold text-stone-900 mb-4">Letzte Strategien</h3>
          {history.length > 0 ? (
            <div className="space-y-3">
              {history.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => loadFromHistory(item)}
                  className="w-full text-left p-4 border border-stone-200 rounded-lg hover:border-fox-fire/50 hover:bg-fox-fire/5 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{item.hauptthema || 'Strategie'}</p>
                      <p className="text-sm text-stone-500">
                        {item.zeitraum} | {item.plattformen?.join(', ')}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-stone-400" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-stone-500 text-center py-8">Noch keine Strategien erstellt</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h2 className="font-heading text-lg font-bold mb-4">Strategie-Parameter</h2>
          
          <div className="space-y-4">
            {/* Zeitraum */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Zeitraum</label>
              <div className="flex gap-2">
                {['1 Woche', '2 Wochen', '1 Monat'].map(option => (
                  <button
                    key={option}
                    onClick={() => setZeitraum(option)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all ${
                      zeitraum === option
                        ? 'border-fox-fire bg-fox-fire/10'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Startdatum */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Startdatum</label>
              <input
                type="date"
                value={startdatum}
                onChange={(e) => setStartdatum(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20"
              />
            </div>
            
            {/* Hauptthema */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Hauptthema *</label>
              <input
                type="text"
                value={hauptthema}
                onChange={(e) => setHauptthema(e.target.value)}
                placeholder="z.B. AVGS-Bewerbungscoaching"
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20"
              />
            </div>
            
            {/* Hauptziel */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Hauptziel</label>
              <select
                value={hauptziel}
                onChange={(e) => setHauptziel(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20"
              >
                <option value="Leads für AVGS-Coaching">Leads für AVGS-Coaching</option>
                <option value="Markenbekanntheit">Markenbekanntheit steigern</option>
                <option value="Community aufbauen">Community aufbauen</option>
                <option value="Engagement erhöhen">Engagement erhöhen</option>
              </select>
            </div>
            
            {/* Zielgruppe */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Zielgruppe</label>
              <select
                value={zielgruppe}
                onChange={(e) => setZielgruppe(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:ring-2 focus:ring-fox-fire/20"
              >
                <option value="Jobcenter-Kunden">Jobcenter-Kunden</option>
                <option value="Arbeitssuchende 25-35">Arbeitssuchende 25-35 Jahre</option>
                <option value="Berufseinsteiger">Berufseinsteiger</option>
                <option value="Quereinsteiger">Quereinsteiger</option>
                <option value="custom">Eigene Eingabe...</option>
              </select>
              {zielgruppe === 'custom' && (
                <input
                  type="text"
                  value={zielgruppeCustom}
                  onChange={(e) => setZielgruppeCustom(e.target.value)}
                  placeholder="Eigene Zielgruppe beschreiben"
                  className="w-full mt-2 px-4 py-2 border border-stone-200 rounded-lg"
                />
              )}
            </div>
            
            {/* Plattformen */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Plattformen</label>
              <div className="flex flex-wrap gap-2">
                {['instagram', 'facebook', 'linkedin', 'tiktok'].map(platform => (
                  <button
                    key={platform}
                    onClick={() => togglePlatform(platform)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                      plattformen.includes(platform)
                        ? 'border-fox-fire bg-fox-fire/10'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    <PlatformIcon platform={platform} size={16} />
                    <span className="capitalize">{platform}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Tonalität */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Tonalität</label>
              <div className="flex gap-2">
                {['motiviert', 'professionell', 'locker'].map(tone => (
                  <button
                    key={tone}
                    onClick={() => setTonalitaet(tone)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all capitalize ${
                      tonalitaet === tone
                        ? 'border-fox-fire bg-fox-fire/10'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    {tone}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Post-Frequenz */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Post-Frequenz</label>
              <select
                value={postFrequenz}
                onChange={(e) => setPostFrequenz(e.target.value)}
                className="w-full px-4 py-2 border border-stone-200 rounded-lg"
              >
                <option value="3 Posts/Woche">3 Posts/Woche</option>
                <option value="5 Posts/Woche">5 Posts/Woche</option>
                <option value="Täglich">Täglich</option>
                <option value="2x täglich">2x täglich</option>
              </select>
            </div>
            
            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Strategie wird erstellt...
                </>
              ) : (
                <>
                  <Sparkles size={20} />
                  Strategie generieren
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Results */}
        <div className="space-y-6">
          {strategy ? (
            <>
              {/* Overview */}
              <div className="bg-gradient-to-br from-fox-fire/10 to-orange-50 rounded-xl border border-fox-fire/20 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="text-green-600" size={20} />
                  <h3 className="font-bold text-stone-900">Strategie erstellt</h3>
                </div>

                <BrandContextHint
                  show={strategy?.meta?.brand_context_used}
                  text="Diese Strategie wurde mit deinem gespeicherten Markenprofil erstellt."
                />

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-stone-500">Zeitraum</p>
                    <p className="font-bold">{strategy.meta?.zeitraum || zeitraum}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-xs text-stone-500">Geplante Posts</p>
                    <p className="font-bold">{strategy.posts?.length || 0}</p>
                  </div>
                </div>
                
                <button
                  onClick={handleImportToBatchPlanner}
                  disabled={importLoading || !strategy.posts?.length}
                  className="w-full bg-fox-fire text-white py-3 rounded-lg hover:bg-fox-fire/90 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {importLoading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <Download size={18} />
                  )}
                  In Batch Planner übernehmen
                </button>
              </div>
              
              {/* Content Plan */}
              {strategy.posts && strategy.posts.length > 0 && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="font-bold text-stone-900 mb-4">Content-Plan ({strategy.posts.length} Posts)</h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {strategy.posts.map((post, idx) => (
                      <div key={idx} className="p-4 border border-stone-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-fox-fire">
                            {post.date} ({post.day_of_week})
                          </span>
                          <div className="flex gap-1">
                            <span className="text-xs px-2 py-0.5 bg-stone-100 rounded capitalize">
                              {post.platform}
                            </span>
                            <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                              {post.content_type}
                            </span>
                          </div>
                        </div>
                        <p className="font-medium text-stone-900">{post.hook}</p>
                        {post.body_outline && (
                          <p className="text-sm text-stone-600 mt-1">{post.body_outline}</p>
                        )}
                        {post.suggested_cta && (
                          <p className="text-xs text-fox-fire mt-2">CTA: {post.suggested_cta}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Strategy Details */}
              {strategy.overview && (
                <div className="bg-white rounded-xl border border-stone-200 p-6">
                  <h3 className="font-bold text-stone-900 mb-4">Strategie-Zusammenfassung</h3>
                  <p className="text-stone-600 whitespace-pre-wrap">
                    {strategy.overview}
                  </p>
                  
                  {/* Weekly Themes */}
                  {strategy.weekly_themes && strategy.weekly_themes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-stone-100">
                      <h4 className="font-medium text-stone-800 mb-2">Wochen-Themen:</h4>
                      <ul className="space-y-1">
                        {strategy.weekly_themes.map((theme, idx) => (
                          <li key={idx} className="text-sm text-stone-600">
                            • Woche {theme.woche}: {theme.thema}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* KPI Focus */}
                  {strategy.kpi_focus && strategy.kpi_focus.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-stone-100">
                      <h4 className="font-medium text-stone-800 mb-2">KPI-Fokus:</h4>
                      <div className="flex flex-wrap gap-2">
                        {strategy.kpi_focus.map((kpi, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded">
                            {kpi}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="bg-stone-50 rounded-xl border border-stone-200 p-12 text-center">
              <Target className="mx-auto mb-4 text-stone-400" size={48} />
              <h3 className="font-bold text-stone-700 mb-2">Noch keine Strategie</h3>
              <p className="text-stone-500">
                Fülle das Formular aus und klicke auf "Strategie generieren"
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StrategieExpertePage;
